"use client";

import useSWR, { mutate as globalMutate } from "swr";
import { useAuth } from "@clerk/nextjs";
import { createFetcher } from "@/lib/api";
import type { ExceptionsResponse, Exception } from "@/lib/api-types";
import { useCallback } from "react";

export function useExceptions() {
  const { getToken } = useAuth();
  const { data, error, isLoading } = useSWR<ExceptionsResponse>(
    "/api/v1/exceptions",
    createFetcher(getToken),
    { refreshInterval: 5000 },
  );
  return {
    exceptions: data?.exceptions ?? [],
    isLoading,
    error,
  };
}

export function useException(id: string | undefined) {
  const { getToken } = useAuth();
  const { data, error, isLoading } = useSWR<Exception>(
    id ? `/api/v1/exceptions/${id}` : null,
    createFetcher(getToken),
  );
  return { exception: data, isLoading, error };
}

export function useResolveException() {
  const { getToken } = useAuth();

  const resolve = useCallback(
    async (id: string, resolution: string) => {
      const token = await getToken();
      const res = await fetch(`/api/v1/exceptions/${id}/resolve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ resolution }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      await globalMutate((key: string) =>
        typeof key === "string" && key.startsWith("/api/v1/exceptions"),
      );
    },
    [getToken],
  );

  return { resolve };
}
